import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppStatusInfo } from '../../../constants/appStatus';

const AdminUserDetailsView = ({
  user,
  applications,
  selectedApplication,
  loading,
  applicationsLoading,
  isUpdatingStatus,
  onApplicationSelect,
  onBackToSettings,
  onDeleteUser,
  onResetPassword}) => {
  const [sendEmail, setSendEmail] = useState(true);

  // Format date for display
  const formatDate = (dateValue) => {
    if (!dateValue) return 'Not available';
    
    if (typeof dateValue.toDate === 'function') {
      return new Date(dateValue.toDate()).toLocaleString();
    } else if (dateValue.seconds) {
      return new Date(dateValue.seconds * 1000).toLocaleString();
    } else if (typeof dateValue === 'number') {
      return new Date(dateValue).toLocaleString();
    }
    
    return 'Invalid date';
  };

  // Get status color class
  const getStatusColorClass = (status) => {
    return `text-${AppStatusInfo[status]?.color || 'gray'}-400`;
  };

  // Get status display name
  const getStatusDisplayName = (status) => {
    return AppStatusInfo[status]?.displayName || 'Unknown';
  };

  // Get deployment status display
  const getDeploymentStatusDisplay = (app) => {
    if (!app.deploymentStatus) return null;
    
    const status = app.deploymentStatus.status;
    const currentStage = app.deploymentStatus.currentStage;
    
    // Find active stage name
    let stageName = '';
    if (currentStage && app.deploymentStatus.stages) {
      // Try to find the stage name from the templates in the deployment status
      const stageObj = Object.entries(app.deploymentStatus.stages)
        .find(([id, stage]) => id === currentStage);
      if (stageObj && stageObj[1].title) {
        stageName = stageObj[1].title;
      }
    }
    
    return {
      status,
      stageName,
      colorClass: status === 'completed' ? 'text-green-400' : 
                  status === 'in-progress' ? 'text-blue-400' : 
                  'text-gray-400'
    };
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  return (
    <div className="bg-gray-900 min-h-screen">
      {/* Header with back button */}
      <div className="bg-gray-800 py-4 px-6 shadow-md mb-6">
        <div className="container mx-auto flex items-center justify-between">
          <button 
            onClick={onBackToSettings}
            className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to System Settings
          </button>
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <div className="w-32">
            {/* Placeholder for symmetry */}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pb-12">
        {/* Loading state */}
        {loading ? (
          <div className="flex justify-center my-12">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : !user ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-400">User not found</p>
            <button 
              onClick={() => {
                console.log('Return to System Settings clicked');
                // Try direct navigation instead of using the callback
                window.location.href = '/admin/system-settings';
              }}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Return to System Settings
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-4 gap-6">
            {/* Left Sidebar - User Details */}
            <div className="md:col-span-1">
              <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 bg-gray-700 rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden">
                    {user.photoURL ? (
                      <>
                        <img 
                          src={user.photoURL} 
                          alt={user.displayName || user.email} 
                          className="w-full h-full object-cover"
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                          onError={(e) => {
                            console.error('Error loading profile image:', e);
                            // מסתיר את התמונה השבורה
                            e.target.style.display = 'none';
                            // מציג את האות הראשונה במקום
                            e.target.parentNode.innerHTML = `<span class="text-3xl text-gray-300">
                              ${user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                            </span>`;
                          }}
                        />
                      </>
                    ) : (
                      <span className="text-3xl text-gray-300">
                        {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-white mb-1">{user.displayName || 'No Name'}</h2>
                  <p className="text-gray-400">{user.email}</p>
                </div>

                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-gray-500">Status</p>
                    <p className={user.isActive ? 'text-green-400' : 'text-red-400'}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-gray-500">Registration Date</p>
                    <p className="text-gray-300">{formatDate(user.createdAt)}</p>
                  </div>
                  
                  <div>
                    <p className="text-gray-500">Last Login</p>
                    <p className="text-gray-300">{formatDate(user.lastLogin)}</p>
                  </div>
                  
                  <div>
                    <p className="text-gray-500">Total Applications</p>
                    <p className="text-blue-400 font-medium">{applications.length}</p>
                  </div>
                </div>

                <div className="mt-8 space-y-3">
                  <button
                    onClick={onResetPassword}
                    className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Reset Password
                  </button>
                  
                  <button
                    onClick={onDeleteUser}
                    className="w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    Delete User
                  </button>
                </div>
              </div>
            </div>

            {/* Main Content - Applications List & Details */}
            <div className="md:col-span-3">
              <div className="bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                <div className="border-b border-gray-700 p-4">
                  <h3 className="text-xl font-semibold text-white">User Applications</h3>
                </div>

                {/* Content grid */}
                <div className="grid md:grid-cols-5">
                  {/* Applications List */}
                  <div className="md:col-span-2 border-r border-gray-700 max-h-[70vh] overflow-y-auto">
                    {applicationsLoading ? (
                      <div className="flex justify-center py-12">
                        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : applications.length === 0 ? (
                      <div className="text-center py-12">
                        <p className="text-gray-400">No applications found for this user</p>
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-700">
                        {applications.map((app) => (
                          <li 
                            key={app.id}
                            onClick={() => onApplicationSelect(app)}
                            className={`p-4 hover:bg-gray-700 cursor-pointer transition-colors ${
                              selectedApplication?.id === app.id ? 'bg-gray-700' : ''
                            }`}
                          >
                            <div className="flex items-start">
                              <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-xs font-medium text-gray-300">{app.fileType?.substring(0, 3).toUpperCase()}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-white truncate text-left">{app.name}</h4>
                                <div className="flex items-center mt-1 justify-start">
                                  <span className={`text-xs ${getStatusColorClass(app.statusApp)}`}>
                                    {getStatusDisplayName(app.statusApp)}
                                  </span>
                                  
                                  <span className="text-gray-500 text-xs mx-2">•</span>
                                  <span className="text-gray-400 text-xs">{formatDate(app.createdAt).split(',')[0]}</span>
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Application Details */}
                  <div className="md:col-span-3 p-6">
                    {!selectedApplication ? (
                      <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                        <svg className="w-16 h-16 text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-gray-400 mb-2">Select an application to view details</p>
                      </div>
                    ) : (
                      <div>
                        <div className="flex justify-between items-center mb-6">
                          <h3 className="text-xl font-semibold text-white">{selectedApplication.name}</h3>
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs ${getStatusColorClass(selectedApplication.statusApp)} bg-opacity-20 bg-gray-700`}>
                              {getStatusDisplayName(selectedApplication.statusApp)}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6 mb-8">
                          <div>
                            <p className="text-gray-500 text-sm mb-1">Upload Date</p>
                            <p className="text-gray-300">{formatDate(selectedApplication.createdAt)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm mb-1">Last Updated</p>
                            <p className="text-gray-300">{formatDate(selectedApplication.updatedAt)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm mb-1">File Size</p>
                            <p className="text-gray-300">{formatFileSize(selectedApplication.fileSize)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm mb-1">File Type</p>
                            <p className="text-gray-300">{selectedApplication.fileType?.toUpperCase() || 'Unknown'}</p>
                          </div>
                        </div>

                        <div className="space-y-4 mt-8">
                          {selectedApplication.fileId && (
                            <Link 
                              to={`/app-analysis/${selectedApplication.fileId}`}
                              className="inline-block w-full text-center py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            >
                              View Analysis
                            </Link>
                          )}
                          
                          {/* Deploy Status Button */}
                          {selectedApplication.fileId && (
                            <Link 
                              to={`/admin/deploy-status/${selectedApplication.fileId}?currentStage=${selectedApplication.deploymentStatus?.currentStage || ''}`}
                              className="inline-block w-full text-center py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                            >
                              Update Deployment Status
                            </Link>
                          )}
                          
                          {/* We could add more actions here like download, delete, etc. */}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserDetailsView; 